import React from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  OutlinedInput,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Labels from "./Labels";
import { isRouteEyeAdmin } from "../../utils/isRouteEyeAdmin";

export default function Application({
  SettingRow,
  formData = {},
  handleChange,
  saving,
  handleMapDetailsChange,
  handleMultilingualLanguagesChange,
  languageChangeDurationOptions = [],
  languages = [],
  handleRequestChange,
  handleLanguageChange,
  backUpDuration = [],
}) {
  const { t } = useTranslation();
  const isSuperAdmin = isRouteEyeAdmin();

  // Get mapDetails from formData
  const mapDetails = formData.mapDetails || [];


  return (
    <Box>
      <Box className="settings-container">
        {/* Date Format */}
        <SettingRow
          label={t("Date format")}
          name="dateFormat"
          value={formData.dateFormat}
          onChange={handleChange}
          options={["DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD", "DD MMM YYYY"]}
          disabled={saving}
        />

        {/* Time Format */}
        <SettingRow
          label={t("Time Format")}
          name="timeFormat"
          value={formData.timeFormat}
          onChange={handleChange}
          options={["12hr", "24hr"]}
          disabled={saving}
        />

        {/* Default Map View */}
        <SettingRow
          label={t("Default map view")}
          name="defaultMapView"
          value={formData.defaultMapView}
          onChange={handleChange}
          options={["Satellite", "Roadmap", "Hybrid", "Terrain"]}
          disabled={saving}
        />

        {/* ========================================
          MAP DETAILS - Only 6 Options
          ========================================== */}
      <Box className="setting-row">
        <Typography className="form-label">{t("Map details")}</Typography>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth disabled={saving}>
            <Select
              multiple
              displayEmpty
              name="mapDetails"
              value={mapDetails}
              onChange={handleMapDetailsChange}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography sx={{ color: "#aaa", fontSize: "14px" }}>
                    Please select an option
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      maxHeight: "70px",
                      overflowY: "auto",
                    }}
                  >
                    {selected.map((value) => {
                      const option = [
                        { value: "STREET_NAMES", label: "Street Names" },
                        { value: "HOTELS", label: "Hotels" },
                        { value: "HOSPITALS", label: "Hospitals" },
                        { value: "RESTAURANTS", label: "Restaurants" },
                        { value: "SHOPPING_MALLS", label: "Shopping Malls" },
                        { value: "PARK_NAMES", label: "Park Names" },
                        { value: "PETROL_PUMPS", label: "Petrol Pumps" },
                      ].find((opt) => opt.value === value);

                      return (
                        <Box
                          key={value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#8db3d8ad",
                            padding: "4px 8px",
                            borderRadius: "16px",
                            fontSize: "14px",
                            textTransform: "none",
                          }}
                        >
                          {option?.label || value.replace(/_/g, " ")}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                sx={{
                  height: "76px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    minHeight: "48px",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflow: "auto",
                      "& .MuiMenuItem-root": {
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 16px",
                      },
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "#a8a8a8",
                        },
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                {/* Only 6 Options */}
                {[
                  { value: "STREET_NAMES", label: "Street Names" },
                  { value: "HOTELS", label: "Hotels" },
                  { value: "HOSPITALS", label: "Hospitals" },
                  { value: "RESTAURANTS", label: "Restaurants" },
                  { value: "SHOPPING_MALLS", label: "Shopping Malls" },
                  { value: "PARK_NAMES", label: "Park Names" },
                  { value: "PETROL_PUMPS", label: "Petrol Pumps" },
                ].map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={mapDetails.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

      {/* Multilingual Languages */}
      {/* <Box className="setting-row">
        <Typography className="form-label">
          {t("Multilingual option on all tracking page")}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth disabled={saving}>
            <Select
              multiple
              displayEmpty
              name="multilingualLanguages"
              value={formData.multilingualLanguages || []}
              onChange={handleMultilingualLanguagesChange}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography sx={{ color: "#aaa", fontSize: "14px" }}>
                    Please select a language
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      maxHeight: "70px",
                      overflowY: "auto",
                    }}
                  >
                    {selected.map((code) => {
                      const lang = languages.find((l) => l.code === code);
                      return (
                        <Box
                          key={code}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#8db3d8ad",
                            padding: "4px 8px",
                            borderRadius: "16px",
                          }}
                        >
                          {lang?.name || code}
                        </Box>
                      );
                    })}
                  </Box>
                )
              }
              sx={{
                height: "76px",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  minHeight: "48px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflow: "auto",
                    "& .MuiMenuItem-root": {
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      padding: "0 16px",
                    },
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c1c1c1",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#a8a8a8",
                      },
                    },
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  <Checkbox
                    checked={
                      formData.multilingualLanguages?.indexOf(lang.code) > -1
                    }
                  />
                  <ListItemText primary={lang.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box> */}

        {/* Language Change Duration */}
        {/* <SettingRow
          label={t("Language change Duration")}
          name="languageSwitchInterval"
          value={formData.languageSwitchInterval}
          onChange={handleChange}
          options={languageChangeDurationOptions.map((opt) => ({
            code: opt.value,
            name: opt.label,
          }))}
          disabled={saving}
        /> */}

        {/* Default Language */}
        <SettingRow
          label={t("Default language")}
          name="defaultLanguage"
          value={formData.defaultLanguage}
          onChange={handleLanguageChange}
          options={languages}
          disabled={saving}
        />

        {/* Data Retention Duration */}
        <Box className="setting-row">
          <Typography className="form-label">
            {t("Data Retention Duration")}
          </Typography>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <Select
                size="small"
                name="dataRetentionPeriod"
                value={formData.dataRetentionPeriod}
                onChange={handleChange}
                disabled={true}
                sx={{ borderRadius: "8px" }}
              >
                {backUpDuration.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleRequestChange}
              variant="text"
              sx={{
                height: "40px",
                backgroundColor: "transparent",
                color: "#0f3b94ff",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
                border: "none",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                padding: "0 8px",
                minWidth: "auto",
              }}
            >
              Request Change
            </Button>
          </Box>
        </Box>
      </Box>
      {isSuperAdmin && (
        <Box
          sx={{
            marginTop: "20px",
            gap: "24px 40px",
            width: "100%",
            border: "1px solid #C8CBD0",
            borderRadius: "12px",
            padding: "30px",
            boxShadow: "-1px 2px 4px 0px #0000001a",
          }}
        >
          <Labels />
        </Box>
      )}
    </Box>
  );
}
